'use client';

import { useEffect, useState } from 'react';

const KEY = 'mercari-search-history';
const STORAGE_EVENT_KEY = 'storage';
const MAX_LENGTH = 12;

export const useSearchHistory = () => {
  const [history, setHistory] = useState<string[]>([]);

  const getParsedHistories = () => {
    const localStorageHistory = globalThis.window?.localStorage.getItem(KEY);
    return localStorageHistory ? JSON.parse(localStorageHistory) : [];
  };

  const dispatchEvent = (newValue: string) => {
    globalThis.window.dispatchEvent(
      new StorageEvent(STORAGE_EVENT_KEY, {
        key: KEY,
        newValue,
      }),
    );
  };

  const sliceByMaxLength = (values: string[], maxLength: number) => {
    return values.length > maxLength ? values.slice(0, maxLength) : [...values];
  };

  const filterByValue = (values: string[], targetValue: string) => {
    return values.filter((value: string) => value !== targetValue);
  };

  const add = (value: string) => {
    const filtered = filterByValue(getParsedHistories(), value);
    const sliced = sliceByMaxLength([value, ...filtered], MAX_LENGTH);

    const newValue = JSON.stringify(sliced);
    globalThis.window.localStorage.setItem(KEY, newValue);
    dispatchEvent(newValue);
  };

  const remove = (value: string) => {
    const filtered = filterByValue(getParsedHistories(), value);
    const sliced = sliceByMaxLength(filtered, MAX_LENGTH);

    const newValue = JSON.stringify(sliced);
    globalThis.window.localStorage.setItem(KEY, newValue);
    dispatchEvent(newValue);
  };

  const clear = () => {
    const newValue = JSON.stringify([]);
    globalThis.window.localStorage.removeItem(KEY);
    dispatchEvent(newValue);
  };

  useEffect(() => {
    // initalized state from localStorage
    setHistory(getParsedHistories());

    // add storage event handler
    const storageEventHandler = (e: StorageEvent) => {
      if (e.key !== KEY || !e.newValue) return;
      setHistory(JSON.parse(e.newValue));
    };

    globalThis.window.addEventListener(STORAGE_EVENT_KEY, storageEventHandler);

    return () => {
      // remove storage event handler
      globalThis.window.removeEventListener(STORAGE_EVENT_KEY, storageEventHandler);
    };
  }, []);

  return {
    searchHistories: history,
    addSearchHistory: add,
    removeSearchHistory: remove,
    clearSearchHistory: clear,
  };
};
